import type { TypeId } from '@unipile/db';
import type {
  EncryptedHistoryCheckpoint as EncryptedHistoryCheckpointDetails,
  EncryptedHistoryOption,
  ProviderAuthenticationIntentResponse,
} from '@unipile/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { useProvider } from '../../../hooks/useProvider';
import { Alert, AlertDescription, AlertTitle } from '../../shadcn/Alert';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup, FieldLabel } from '../../shadcn/Field';
import { Input } from '../../shadcn/Input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../shadcn/InputOTP';
import { Item, ItemContent, ItemDescription, ItemTitle } from '../../shadcn/Item';
import { RadioGroup, RadioGroupItem } from '../../shadcn/RadioGroup';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/** Messenger backup PINs are six digits, which is what lets the entry be slotted like a one-time code. */
const PIN_LENGTH = 6;

/**
 * How many `…RECOVERY_CODE.step_N` keys the screen looks for. A recovery code is buried several menus deep and
 * nobody knows it by heart, so the path is spelled out rather than summarised in the option's one-line blurb.
 * Providers declare as many steps as they need and leave the rest out; missing keys are simply skipped.
 */
const MAX_RECOVERY_CODE_STEPS = 8;

/**
 * A checkpoint offering to unlock a provider's encrypted message history, with the credential of the user's choice
 * or not at all.
 *
 * One screen for the whole decision: picking an option reveals the field it needs, and a wrong credential comes
 * back as this same checkpoint with one attempt fewer, so the user can retry, switch method or give up without
 * losing the account — it is already authenticated by the time this is asked.
 */
export function EncryptedHistoryCheckpoint({
  checkpoint,
  onSuccess,
  onError,
  intent_id,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  checkpoint: EncryptedHistoryCheckpointDetails;
  intent_id: TypeId<'account'>;
}) {
  const { t } = useTranslation();
  const { provider } = useProvider();
  const [selected, setSelected] = useState<EncryptedHistoryOption>();
  const [credential, setCredential] = useState('');

  const { mutate, isPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess: (response) => {
        // An answer that comes back as a checkpoint is a refused credential: return to the list rather than leave
        // the user on the field that just failed, since switching method is usually the way out — and on a PIN
        // each further try eats into a budget that can lock the backup for good. Reset here rather than from the
        // new props: with a budget per credential, none of them necessarily changes between two refusals.
        if (response.object === 'AuthenticationCheckpoint') {
          setSelected(undefined);
          setCredential('');
        }
        onSuccess(response);
      },
      onError,
    },
  });

  const failedOptionAttempts = checkpoint.last_attempt
    ? checkpoint.options.find((option) => option.id === checkpoint.last_attempt?.option)?.attempts_remaining
    : undefined;

  /**
   * Which message the failure deserves. Three cases, and they are not interchangeable: one method being spent
   * says nothing about the others, so promising the account will be connected without history would be wrong as
   * long as a credential is still open.
   */
  const errorKey =
    checkpoint.attempts_remaining < 1
      ? 'exhausted'
      : failedOptionAttempts === 0
        ? 'option_disabled'
        : checkpoint.last_attempt?.error;

  // Once a credential is picked the list steps aside and only its field is shown, so there is one thing to do on
  // screen. SKIP needs no field, so it stays in the list and simply arms the button.
  const entering = selected === 'PIN' || selected === 'RECOVERY_CODE';

  // Steps a provider chose to document, in order, stopping at the first it did not declare.
  const recoveryCodeSteps = Array.from({ length: MAX_RECOVERY_CODE_STEPS }, (_, index) =>
    t(`ENCRYPTED_HISTORY.RECOVERY_CODE.step_${index + 1}`, { ns: provider, defaultValue: '' }),
  ).filter((step) => step.length > 0);

  // An option the provider has no attempts left for stays visible but unusable: hiding it would leave the user
  // wondering where their PIN went, where the disabled state can say why.
  const exhausted = (option: EncryptedHistoryCheckpointDetails['options'][number]) => option.attempts_remaining === 0;

  // A slotted PIN is only worth submitting once every digit is in: a short one would spend an attempt for nothing.
  const isCredentialComplete =
    selected === 'SKIP' || (selected === 'PIN' ? credential.length === PIN_LENGTH : credential.trim().length > 0);

  const submit = () => {
    if (!selected) return;
    mutate({
      data: { intent_id, code: selected, ...(selected === 'SKIP' ? {} : { credential: credential.trim() }) },
    });
  };

  return (
    <WizardStepContainer
      title={t('ENCRYPTED_HISTORY.title', { ns: provider })}
      description={t('ENCRYPTED_HISTORY.description', { ns: provider })}
    >
      {checkpoint.last_attempt && (
        <Alert variant="red" className="mb-6">
          <AlertTitle>
            {t(`ENCRYPTED_HISTORY.error.${errorKey}.title`, {
              ns: provider,
              method: t(`ENCRYPTED_HISTORY.${checkpoint.last_attempt.option}.label`, { ns: provider }),
            })}
          </AlertTitle>
          <AlertDescription>
            {/* Out of attempts the wording changes: there is nothing left to retry, only to acknowledge. */}
            {t(`ENCRYPTED_HISTORY.error.${errorKey}.description`, {
              ns: provider,
              // The budget of the credential that just failed, not the overall one: with a count per credential
              // the overall figure is whichever is highest, so it stays put while the failing one runs down.
              attempts: failedOptionAttempts ?? checkpoint.attempts_remaining,
              provider_attempts: checkpoint.provider_attempts_remaining,
              method: t(`ENCRYPTED_HISTORY.${checkpoint.last_attempt.option}.label`, { ns: provider }),
            })}
          </AlertDescription>
        </Alert>
      )}

      <FieldGroup>
        {!entering && (
          <Field>
            <RadioGroup
              value={selected ?? ''}
              onValueChange={(value) => {
                const option = checkpoint.options.find((candidate) => candidate.id === value);
                if (!option) return;
                setSelected(option.id);
                setCredential('');
              }}
              className="gap-2"
            >
              {checkpoint.options.map((option) => (
                <Item key={option.id} asChild variant="outline">
                  <label
                    htmlFor={option.id}
                    className="has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:ring-primary/30 cursor-pointer has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[[data-state=checked]]:ring-2"
                  >
                    <RadioGroupItem value={option.id} id={option.id} disabled={exhausted(option)} />
                    <ItemContent>
                      <ItemTitle>{option.label}</ItemTitle>
                      {option.description && <ItemDescription>{option.description}</ItemDescription>}
                    </ItemContent>
                  </label>
                </Item>
              ))}
            </RadioGroup>
          </Field>
        )}

        {selected === 'PIN' && (
          <Field>
            <FieldLabel>{t('ENCRYPTED_HISTORY.PIN.label', { ns: provider })}</FieldLabel>
            {/* Same slotted entry as a one-time code: a backup PIN is six digits, and typing it into a bare text
                field gives no sense of how many are expected. */}
            <InputOTP
              maxLength={PIN_LENGTH}
              containerClassName="w-full justify-center"
              value={credential}
              onChange={setCredential}
            >
              <InputOTPGroup className="w-full justify-center">
                {Array.from({ length: PIN_LENGTH }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </Field>
        )}

        {selected === 'RECOVERY_CODE' && (
          <Field>
            {recoveryCodeSteps.length > 0 && (
              <Item variant="muted" size="sm" className="mb-2">
                <ItemContent>
                  <ItemTitle>{t('ENCRYPTED_HISTORY.RECOVERY_CODE.help', { ns: provider })}</ItemTitle>
                  <ol className="text-muted-foreground list-decimal space-y-1 pl-4">
                    {recoveryCodeSteps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </ItemContent>
              </Item>
            )}
            <FieldLabel>{t('ENCRYPTED_HISTORY.RECOVERY_CODE.label', { ns: provider })}</FieldLabel>
            <Input autoComplete="off" value={credential} onChange={(event) => setCredential(event.target.value)} />
          </Field>
        )}

        <Button className="mt-8 w-full" type="button" onClick={submit} disabled={isPending || !selected || !isCredentialComplete}>
          {isPending && <Spinner />}
          {t('Common.next')}
        </Button>

        {/* Without this the field is a dead end: a user who picked the wrong method, or who realises they do not
            have that credential, has no way back to the other two. */}
        {entering && (
          <Button
            className="w-full"
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => {
              setSelected(undefined);
              setCredential('');
            }}
          >
            {t('ENCRYPTED_HISTORY.back', { ns: provider })}
          </Button>
        )}
      </FieldGroup>
    </WizardStepContainer>
  );
}
